import { forwardRef, PropsWithChildren, ReactNode } from 'react';

import {
  Box,
  Button,
  ButtonProps,
  TabList,
  TabListProps,
  TabPanel,
  TabPanels,
  Tabs,
  useMediaQuery,
  useTab,
} from '@chakra-ui/react';
import { focusStyles } from '@udacity/chakra-uds-theme';

export type ChipTabProps = {
  tabLabels: string[];
  tabPanels: ReactNode[];
  tabListProps?: TabListProps;
  tabLabelProps?: ButtonProps;
  spacerHeight?: string;
  vertical?: boolean;
};

export function ChipTabs({
  tabLabels = [],
  tabPanels = [],
  tabListProps,
  tabLabelProps,
  spacerHeight = '40px',
  vertical = false,
}: ChipTabProps) {
  const [isDesktop] = useMediaQuery('(min-width: 1280px)');

  if (!isDesktop) {
    vertical = false;
  }

  return (
    <Tabs
      alignItems='flex-start'
      gap='10px'
      orientation={vertical ? 'vertical' : 'horizontal'}
      position='relative'
      variant='soft-rounded'
      isLazy
    >
      <TabList
        display='flex'
        gap='8px'
        overflowX='auto'
        flexDirection={{
          base: 'row',
          lg: 'column',
        }}
        flexWrap={{
          lg: 'wrap',
        }}
        justifyContent={{
          lg: 'center',
        }}
        maxWidth={{
          lg: '900px',
        }}
        padding={{
          base: '5px 0',
          md: '0',
        }}
        width={{
          base: '100%',
          lg: vertical ? '360px' : '100%',
        }}
        {...tabListProps}
      >
        {tabLabels.map((label, idx) => (
          <ChipTab key={idx} {...tabLabelProps}>
            {label}
          </ChipTab>
        ))}
      </TabList>
      <Box h={spacerHeight} />
      <TabPanels>
        {tabPanels.map((panel, idx) => (
          <TabPanel key={idx} padding='0px' tabIndex={-1}>
            {panel}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}

const ChipTab = forwardRef<HTMLButtonElement, PropsWithChildren>((props, ref) => {
  const tabProps = useTab({ ...props, ref });
  return (
    <Button
      backgroundColor='white'
      border='1px solid'
      borderColor='gray.300'
      borderRadius='xl'
      color='black'
      fontWeight='600'
      height='fit-content'
      minWidth='min-content'
      padding='10px 16px'
      whiteSpace='nowrap'
      _hover={{
        backgroundColor: 'accent.sky.100',
      }}
      _selected={{
        borderColor: 'blue.500',
        backgroundColor: 'blue.500',
      }}
      {...focusStyles('blue.500', 'full')}
      {...tabProps}
      overflow='hidden'
    >
      {tabProps.children}
    </Button>
  );
});

ChipTab.displayName = 'ChipTab';
